import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause, Loader2, Waves, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ShareSheet from '../components/ShareSheet';
import { useAudio } from '../context/AudioContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * SoundscapesPage — DB-driven soundscapes routed through the central
 * AudioContext. Every soundscape:
 *   - streams the real MP3 from Emergent Object Storage via `/api/media/{id}`
 *   - loops by default (loop: true on the shared HTMLAudioElement)
 *   - opens the mini/full player used by the rest of the app so only ONE
 *     primary audio source plays at a time
 *   - keeps playing when the user navigates away — the mini-player takes
 *     over and offers Sleep Timer + close from anywhere
 *
 * The old video-based player is gone (per Prompt #4 Q3 answer). If a
 * soundscape ever lacks a real audio asset, we hide it rather than fake
 * playback (Rule 42, Rule 103).
 */
const SoundscapesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state, play, toggle } = useAudio();
  const [items, setItems] = useState([]);
  const [shareItem, setShareItem] = useState(null);
  const shareFor = (sc) => ({
    kind: 'soundscape',
    id: sc.id,
    title: sc.title,
    text: `"${sc.title}" — a calming soundscape on Reflections by Rowdy`,
    url: `${window.location.origin}/sounds/${sc.id}`,
    recommendable: true,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/content?type=soundscape&limit=50`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((data) => {
        if (cancelled) return;
        setItems((data.items || []).filter((c) => c.audioAssetId || c.audioUrl));
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const activate = (sc) => {
    const src = sc.audioUrl || (sc.audioAssetId ? `${API_URL}/api/media/${sc.audioAssetId}` : null);
    if (!src) return toast.error('This soundscape has no audio yet.');
    // If this exact soundscape is already active, just toggle play/pause.
    if (state.current?.contentId === sc.id) {
      toggle();
      return;
    }
    play({
      src,
      title: sc.title,
      subtitle: 'Soundscape · loops seamlessly',
      artwork: sc.coverImageUrl,
      kind: 'soundscape',
      contentId: sc.id,
      sourceContext: 'soundscapes',
      loop: true,
    });
  };

  const isPlayingThis = (sc) => state.current?.contentId === sc.id && state.playing;

  return (
    <div className="pb-32 min-h-screen bg-[#F6F1E7]" data-testid="soundscapes-page">
      <div className="px-5 pt-5 pb-3">
        <button
          onClick={() => (id ? navigate('/sounds') : navigate('/more'))}
          className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-stone-700 mb-3"
          data-testid="soundscapes-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <p className="rbr-eyebrow mb-2" style={{ color: '#C79A4A' }}>Soundscapes</p>
        <h1
          className="text-3xl leading-tight text-stone-800 mb-1"
          style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
        >
          Steady sound, softly held.
        </h1>
        <p className="text-sm text-stone-500">
          One hour of real ambient audio for focus, sleep, or a quiet room.
          Loops seamlessly. Set a sleep timer from the player when you&rsquo;re ready to drift off.
        </p>
      </div>

      <div className="px-4 py-4">
        {loading ? (
          <p className="text-center py-10 text-stone-500">Loading soundscapes…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-14 px-6">
            <Waves className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Soundscapes are coming soon.
            </p>
            <p className="text-[12px] text-stone-500 mt-1">Rowdy is preparing something restful.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((sc) => {
              const playing = isPlayingThis(sc);
              const active = state.current?.contentId === sc.id;
              const loading = active && state.loading;
              return (
                <button
                  key={sc.id}
                  onClick={() => activate(sc)}
                  data-testid={`soundscape-card-${sc.slug || sc.id}`}
                  className="relative rounded-2xl overflow-hidden text-left group active:scale-[0.99] transition-transform"
                  style={{ minHeight: 180 }}
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${sc.coverImageUrl || ''})` }}
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: 'linear-gradient(180deg, rgba(13,43,36,0.10) 0%, rgba(13,43,36,0.85) 100%)' }}
                  />
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setShareItem(shareFor(sc)); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setShareItem(shareFor(sc)); } }}
                    data-testid={`soundscape-share-${sc.slug || sc.id}`}
                    aria-label="Share"
                    className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(13,43,36,0.45)', color: '#F6F1E7', backdropFilter: 'blur(6px)' }}
                  >
                    <Share2 className="w-4 h-4" />
                  </span>
                  <div className="relative h-full flex flex-col justify-end p-4 min-h-[180px]" style={{ color: '#F6F1E7' }}>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>
                      {sc.category || 'Soundscape'}
                    </p>
                    <p
                      className="text-xl leading-snug mt-0.5"
                      style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif" }}
                    >
                      {sc.title}
                    </p>
                    <p className="text-[11px] mt-1 line-clamp-2" style={{ color: 'rgba(246,241,231,0.75)' }}>
                      {sc.shortDescription}
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                        style={{ background: 'rgba(199,154,74,0.22)', color: '#C79A4A', border: '1px solid rgba(199,154,74,0.4)' }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#C79A4A' }} />
                        1 hour · loops
                      </span>
                      <span
                        className="w-11 h-11 rounded-full flex items-center justify-center"
                        style={{ background: playing ? '#C79A4A' : 'rgba(246,241,231,0.15)', color: playing ? '#0D2B24' : '#F6F1E7', backdropFilter: 'blur(6px)' }}
                        aria-label={playing ? 'Pause' : 'Play'}
                      >
                        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <ShareSheet open={!!shareItem} onClose={() => setShareItem(null)} content={shareItem} />
    </div>
  );
};

export default SoundscapesPage;
